"use client";

import {
  ConvertToShamsiDate,
  FormatNumberWithSeparator,
  genericCopyToClipboard
} from "@/utils/utils";
import { Tag, Share2, Bookmark } from "lucide-react";
import { IconButton } from "@/components/icon-button";
import { useUserStore } from "@/store/userStore";
import { shouldShow } from "@/types/utils";

interface FileHeaderProps {
  title: string;
  adid: string;
  propertyType: string;
  listingType: string;
  totalPrice: string;
  colleaguePrice: string;
  minPrice: string;
  maxPrice: string;
  mortgageAmount: string;
  rentAmount: string;
  lastMortgageAmount: string;
  lastRentAmount: string;
  createdAt: string;
  maxMortgageAmount: string;
  minMortgageAmount: string;
  maxRentAmount: string;
  minRentAmount: string;
}

export const FileHeader: React.FC<FileHeaderProps> = ({
  title,
  adid,
  propertyType,
  listingType,
  totalPrice,
  colleaguePrice,
  minPrice,
  maxPrice,
  mortgageAmount,
  rentAmount,
  lastMortgageAmount,
  lastRentAmount,
  createdAt,
  maxMortgageAmount,
  minMortgageAmount,
  maxRentAmount,
  minRentAmount,
}) => {
  const { is_colleague } = useUserStore();

  const handleShareClick = (e: React.MouseEvent<HTMLElement>) => {
    const slug = title.trim().replaceAll(/\s+/g, "-");
    genericCopyToClipboard(`${window.location.host}/${slug}?id=${adid}`, e);
  };

  return (
    <div className="w-full flex flex-col gap-3">
      {/* Title + Property Type */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <h1 className="text-[18px] sm:text-[20px] font-bold text-gray-900">
          {title}
        </h1>
        <span className="bg-gray-100 text-gray-700 px-4 py-1 rounded text-sm font-bold w-fit">
          {propertyType}
        </span>
      </div>

      {/* Ad Type + Price section */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-1 gap-3 sm:gap-0">
        {/* Left section */}
        <div className="flex flex-col sm:flex-row sm:gap-6 gap-3">
          {/* Ad Type */}
          <span className="bg-[#F97066] text-white px-4 py-2 rounded text-sm font-semibold w-fit">
            {listingType}
          </span>

          {/* Total Price */}
          {shouldShow(totalPrice) && (
            <div className="flex gap-2 items-center">
              <span className="flex items-center gap-1 text-gray-500 text-sm sm:text-base">
                <Tag size={18} className="sm:w-5 sm:h-5" /> قیمت
              </span>
              <span className="text-blue-800 font-bold text-base sm:text-lg">
                {FormatNumberWithSeparator(totalPrice)} تومان
              </span>
            </div>
          )}

          {/* Min / Max Price */}
          {!shouldShow(totalPrice) && (shouldShow(minPrice) || shouldShow(maxPrice)) && (
            <div className="flex gap-2 items-center">
              <span className="flex items-center gap-1 text-gray-500 text-sm sm:text-base">
                <Tag size={18} className="sm:w-5 sm:h-5" /> قیمت
              </span>
              <span className="text-blue-800 font-bold text-base sm:text-lg">
                {[
                  shouldShow(minPrice) ? FormatNumberWithSeparator(minPrice) : null,
                  shouldShow(maxPrice) ? FormatNumberWithSeparator(maxPrice) : null,
                ].filter(Boolean).join(" - ")} تومان
              </span>
            </div>
          )}

          {/* Colleague price */}
          {is_colleague && shouldShow(colleaguePrice) &&
            <div className="flex gap-2 items-center">
              <span className="flex items-center gap-1 text-gray-500 text-sm sm:text-base">
                <Tag size={18} className="sm:w-5 sm:h-5" /> قیمت همکار
              </span>
              <span className="text-blue-800 font-bold text-base sm:text-lg">
                {FormatNumberWithSeparator(colleaguePrice)} تومان
              </span>
            </div>
          }

          {/* Mortgage */}
          {shouldShow(mortgageAmount) && (
            <div className="flex gap-2 items-center">
              <span className="flex items-center gap-1 text-gray-500 text-sm sm:text-base">
                <Tag size={18} className="sm:w-5 sm:h-5" /> رهن
              </span>
              <span className="text-blue-800 font-bold text-base sm:text-lg">
                {FormatNumberWithSeparator(mortgageAmount)} تومان
              </span>
            </div>
          )}

          {(shouldShow(maxMortgageAmount) || shouldShow(minMortgageAmount)) && (
            <div className="flex gap-2 items-center">
              <span className="flex items-center gap-1 text-gray-500 text-sm sm:text-base">
                <Tag size={18} className="sm:w-5 sm:h-5" /> ودیعه
              </span>
              <span className="text-blue-800 font-bold text-base sm:text-lg">
                {[
                  shouldShow(minMortgageAmount) ? FormatNumberWithSeparator(minMortgageAmount) : null,
                  shouldShow(maxMortgageAmount) ? FormatNumberWithSeparator(maxMortgageAmount) : null,
                ].filter(Boolean).join(" - ")} تومان
              </span>
            </div>
          )}

          {/* Rent */}
          {shouldShow(rentAmount) && (
            <div className="flex gap-2 items-center">
              <span className="flex items-center gap-1 text-gray-500 text-sm sm:text-base">
                <Tag size={18} className="sm:w-5 sm:h-5" /> اجاره
              </span>
              <span className="text-blue-800 font-bold text-base sm:text-lg">
                {FormatNumberWithSeparator(rentAmount)} تومان
              </span>
            </div>
          )}

          {(shouldShow(maxRentAmount) || shouldShow(minRentAmount)) && (
            <div className="flex gap-2 items-center">
              <span className="flex items-center gap-1 text-gray-500 text-sm sm:text-base">
                <Tag size={18} className="sm:w-5 sm:h-5" /> ماهانه
              </span>
              <span className="text-blue-800 font-bold text-base sm:text-lg">
                {[
                  shouldShow(minRentAmount) ? FormatNumberWithSeparator(minRentAmount) : null,
                  shouldShow(maxRentAmount) ? FormatNumberWithSeparator(maxRentAmount) : null,
                ].filter(Boolean).join(" - ")} تومان
              </span>
            </div>
          )}

          {/* Last mortgage */}
          {shouldShow(lastMortgageAmount) && (
            <div className="flex gap-2 items-center">
              <span className="flex items-center gap-1 text-gray-500 text-sm sm:text-base">
                <Tag size={18} className="sm:w-5 sm:h-5" /> رهن قبلی
              </span>
              <span className="text-blue-800 font-bold text-base sm:text-lg">
                {FormatNumberWithSeparator(lastMortgageAmount)} تومان
              </span>
            </div>
          )}

          {/* Last rent */}
          {shouldShow(lastRentAmount) && (
            <div className="flex gap-2 items-center">
              <span className="flex items-center gap-1 text-gray-500 text-sm sm:text-base">
                <Tag size={18} className="sm:w-5 sm:h-5" /> اجاره قبلی
              </span>
              <span className="text-blue-800 font-bold text-base sm:text-lg">
                {FormatNumberWithSeparator(lastRentAmount)} تومان
              </span>
            </div>
          )}
        </div>

        {/* Created Date */}
        {createdAt && (
          <span className="flex items-center gap-4 text-gray-400 text-xs sm:text-base w-fit">
            ایجاد: {ConvertToShamsiDate(createdAt)}
            <IconButton
              color="secondary"
              onClick={(e) => handleShareClick(e)}>
              <Share2 size={28} />
            </IconButton>
            <IconButton color="secondary">
              <Bookmark size={28} />
            </IconButton>
          </span>
        )}
      </div>
    </div>
  );
};
